import { appConfig } from '../request/app.config';
import { BACKGROUND_NETWORK_REQ_UI_OPTIONS } from '../request/constants/request-options';
import { NetworkRequestOptions } from '../request/interfaces/request-options';
import { courierService } from '../request/services/courier.service';
import { GenericApiResponse } from '../shared/interfaces/generics';
import {
  EmailRecipientPayload,
  Recipient,
  SlackChannelPayload
} from './ManageRecipients/interface';
import { PreferencesMetaData, RawPreference } from './Preferences/Interface';
import { RawSubscriptionsList, RawSubscription } from './Subscriptions/Interface';

export const NotificationsAPI = {
  alertsServiceURL: appConfig.getAlertServiceURL(),
  securityServiceURL: appConfig.getSecurityServiceURL(),
  ActivityLogsURL: appConfig.getActivityLogsURL(),

  getSubscribedPipelinesList() {
    const requestURL = `${this.alertsServiceURL}/subscriptions`;

    return courierService.get<GenericApiResponse<RawSubscriptionsList>>(requestURL, {});
  },

  unSubscribePipelines(data: RawSubscription[]) {
    const requestURL = `${this.alertsServiceURL}/subscriptions/delete`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.post<GenericApiResponse<RawSubscriptionsList>>(requestURL, data, options);
  },

  subscribePipelines(data: RawSubscription[]) {
    const requestURL = `${this.alertsServiceURL}/subscriptions/add`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.post(requestURL, data, options);
  },

  getChannelsList() {
    const requestURL = `${this.alertsServiceURL}/slack`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.get(requestURL, options);
  },

  deleteChannel(integrationId: string) {
    const requestURL = `${this.securityServiceURL}/webhook-urls/${integrationId}`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.delete(requestURL, options);
  },

  getUserPreferencesMetaData() {
    const requestURL = `${this.alertsServiceURL}/user-preferences/metadata`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.get<GenericApiResponse<PreferencesMetaData>>(requestURL, options);
  },

  getPreferences() {
    const requestURL = `${this.alertsServiceURL}/user-preferences`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.get<GenericApiResponse<{ preferences: RawPreference[] }>>(
      requestURL,
      options
    );
  },

  savePreferences(preferences: RawPreference[]) {
    const requestURL = `${this.alertsServiceURL}/user-preferences`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true, showSuccessMsg: true }
    };

    return courierService.post<GenericApiResponse<{ preferences: RawPreference[] }>>(
      requestURL,
      preferences,
      options
    );
  },

  getSlackAuthUrl(nextUrl: string) {
    const slackAuthUrl = `${this.securityServiceURL}/oauth/slack/authenticate`;

    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true },

      networkOptions: {
        params: new URLSearchParams({
          next: nextUrl,
          is_recipient: 'true',
          environment: 'edge'
        })
      }
    };

    return courierService.get(slackAuthUrl, options);
  },

  getAuthorsList() {
    const authorListURl = `${this.ActivityLogsURL}/filter-options`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS }
    };

    return courierService.get(authorListURl, options);
  },

  // recipient APIs

  addRecipient(payload: SlackChannelPayload | EmailRecipientPayload) {
    const requestURL = `${this.alertsServiceURL}/recipient/add`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true, showSuccessMsg: true }
    };

    return courierService.post<GenericApiResponse<Recipient>>(requestURL, payload, options);
  },

  getRecipientsList() {
    const requestURL = `${this.alertsServiceURL}/recipients`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.get(requestURL, options);
  },

  deleteRecipient(recipientId: string) {
    const requestURL = `${this.alertsServiceURL}/recipient/${recipientId}`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.delete(requestURL, options);
  },

  getRecipientSubscribedPipelinesList(recipientId: string) {
    const requestURL = `${this.alertsServiceURL}/recipient/${recipientId}/subscriptions`;

    return courierService.get<GenericApiResponse<RawSubscriptionsList>>(requestURL, {});
  },

  unSubscribePipelinesForRecipient(data: RawSubscription[], recipientId: string) {
    const requestURL = `${this.alertsServiceURL}/recipient/${recipientId}/subscriptions/delete`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.post<GenericApiResponse<RawSubscriptionsList>>(requestURL, data, options);
  },

  subscribePipelinesForRecipient(data: RawSubscription[], recipientId: string) {
    const requestURL = `${this.alertsServiceURL}/recipient/${recipientId}/subscriptions`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.post<GenericApiResponse<RawSubscriptionsList>>(requestURL, data, options);
  },

  getPreferencesForRecipient(recipientId: string) {
    const requestURL = `${this.alertsServiceURL}/recipient/${recipientId}/preferences`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true }
    };

    return courierService.get<GenericApiResponse<{ preferences: RawPreference[] }>>(
      requestURL,
      options
    );
  },

  savePreferencesForRecipient(preferences: RawPreference[], recipientId: string) {
    const requestURL = `${this.alertsServiceURL}/recipient/${recipientId}/preferences`;
    const options: NetworkRequestOptions = {
      uiOptions: { ...BACKGROUND_NETWORK_REQ_UI_OPTIONS, showErrorMsg: true, showSuccessMsg: true }
    };

    return courierService.put<GenericApiResponse<{ preferences: RawPreference[] }>>(
      requestURL,
      preferences,
      options
    );
  }
};
